import { useCallback, useEffect, useRef, useState } from 'react';
import type { Theme } from '@sokdaq/protocol';
import { avatarFor } from './avatar.js';
import { RoomHeader } from './components/RoomHeader.js';
import { UpdateBanner } from './components/UpdateBanner.js';
import { useAppHeight } from './hooks/useAppHeight.js';
import { useNotifications } from './hooks/useNotifications.js';
import { useWindowTitle } from './hooks/useWindowTitle.js';
import { platform } from './platform/bridge.js';
import { JoinScreen } from './screens/JoinScreen.js';
import { useChatStore } from './store/chat.js';
import { useSettingsStore } from './store/settings.js';
import {
  clearLastRoom,
  loadIdentity,
  loadLastRoom,
  loadMemberToken,
} from './store/persist.js';
import { useUiStore } from './store/ui.js';
import { ClassicView } from './views/ClassicView.js';
import { OfficeView } from './views/OfficeView.js';
import { StealthView } from './views/StealthView.js';

type Screen =
  /** 저장된 세션을 확인하는 중. 한 프레임 스쳐가는 상태다. */
  | { kind: 'loading' }
  | { kind: 'join'; notice: string | null }
  | { kind: 'room'; code: string };

export function App() {
  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });

  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const toggleStealth = useUiStore((s) => s.toggleStealth);
  const syncDesktopState = useUiStore((s) => s.syncDesktopState);
  const setUpdateReady = useUiStore((s) => s.setUpdateReady);
  const status = useChatStore((s) => s.status);
  const statusDetail = useChatStore((s) => s.statusDetail);
  const enterRoom = useChatStore((s) => s.enter);
  const leaveRoom = useChatStore((s) => s.leave);

  const enter = useCallback(
    async (code: string, nickname: string) => {
      const memberToken = await loadMemberToken(code);
      // 직접 고른 아바타가 있으면 그것으로 들어간다. 없으면 닉네임에서
      // 뽑는다 — 처음 켠 사람도 색이 있는 채로 시작한다.
      const avatar = useSettingsStore.getState().avatar ?? avatarFor(nickname);
      enterRoom(code, nickname, avatar, memberToken);
      setScreen({ kind: 'room', code });
    },
    [enterRoom],
  );

  const leave = useCallback(() => {
    leaveRoom();
    void clearLastRoom();
    setScreen({ kind: 'join', notice: null });
  }, [leaveRoom]);

  // 마지막에 있던 방으로 자동 복귀한다. 매일 아침 켜는 앱이라
  // 코드를 다시 입력하게 하면 그것만으로 안 켜게 된다.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current) return;
    resumed.current = true;

    void (async () => {
      const [code, identity] = await Promise.all([loadLastRoom(), loadIdentity()]);
      if (code !== null && identity !== null) {
        await enter(code, identity.nickname);
      } else {
        setScreen({ kind: 'join', notice: null });
      }
    })();
  }, [enter]);

  // 재시도가 소용없는 실패(강퇴, 방 없음, 버전 불일치)면 입장 화면으로
  // 돌려보낸다. 여기 남아 있어 봐야 할 수 있는 게 없다.
  useEffect(() => {
    if (status === 'failed') {
      void clearLastRoom();
      setScreen({ kind: 'join', notice: statusDetail });
    }
  }, [status, statusDetail]);

  useWindowTitle();
  useNotifications();
  useAppHeight();

  useEffect(() => platform.registerBossKey(toggleStealth), [toggleStealth]);

  // 저장된 창 설정을 읽어 UI에 맞추고, 트레이 메뉴로 바뀔 때도 따라간다.
  useEffect(() => {
    void platform.getDesktopState().then(syncDesktopState);
    return platform.onDesktopStateChanged?.(syncDesktopState);
  }, [syncDesktopState]);

  useEffect(() => platform.onUpdateReady?.(setUpdateReady), [setUpdateReady]);

  useEffect(() => {
    const order: Theme[] = ['office', 'stealth', 'classic'];
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey) return;
      const index = ['Digit1', 'Digit2', 'Digit3'].indexOf(event.code);
      if (index === -1) return;
      event.preventDefault();
      setView(order[index]!);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setView]);

  if (screen.kind === 'loading') {
    return <div className="app-shell bg-neutral-950" />;
  }

  if (screen.kind === 'join') {
    return <JoinScreen onEnter={(code, nickname) => void enter(code, nickname)} notice={screen.notice} />;
  }

  return (
    <div className="app-shell flex flex-col bg-neutral-950 text-neutral-100">
      {/* 스텔스 뷰는 화면 전체를 위장한다. 머리글이 남아 있으면 의미가 없다. */}
      {view !== 'stealth' && <RoomHeader onLeave={leave} />}
      <UpdateBanner />

      <main className="min-h-0 flex-1">
        {view === 'office' && <OfficeView />}
        {view === 'stealth' && <StealthView />}
        {view === 'classic' && <ClassicView />}
      </main>
    </div>
  );
}
